/**
 * Sidebar Queue Panel Component
 *
 * @module modules/queue/presentation/shared/SidebarQueuePanel
 *
 * Queue panel embedded in sidebar with tabs, controls, and case list.
 * Includes real-time Socket.IO updates and drag-and-drop reordering.
 */

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { alpha, useTheme as useMuiTheme } from '@mui/material/styles';
import QueueIcon from '@mui/icons-material/Queue';

import Link from '@mui/material/Link';
import { CallHistoryCardList } from '@/modules/call-history';
import { WorkTimeToggle, useWorkTimeType } from '@/modules/workTime';
import { memo, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors } from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { useSocketIOContext } from '@/shared/services/socketio';
import { SocketIONamespace } from '@/shared/services/socketio/types';
import { useToast } from '@/shared/components/toasts';
import { ShortcutBadge } from '@/shared/components/ShortcutBadge';
import { useTelephonyHotkeys } from '@/shared/hooks';
import { CaseCard } from '../components/CaseCard';
import { FollowUpList } from '../components/FollowUpList';
import { PhoneCallSummaryModal } from '../components/PhoneCallSummaryModal';
import { useQueueStore } from '../../application/store';
import { useQueueList, useMoveQueue, useDeleteQueues, useMakeNextCall } from '../../application';
import { v2CallResponseToQueueModel } from '../../application/mappers/v2CallResponseToQueueModel';
import { QueueTab } from '../../domain/types';
import type { QueueModel, CallQueuePaginatedRequest, CallQueueDeleteRequest } from '../../domain/types';
import type { V2CallResponse, CallStatusEventData } from '@/shared/services/socketio/types';

// ============================================================================
// Default Request
// ============================================================================

const sidebarRequest: CallQueuePaginatedRequest = {
  page: 1,
  per_page: 50, // Get more items for sidebar
  include_ended: false,
  all_call_queues: false,
};

/**
 * Parse amount string to number, handling currency symbols and formatting
 */
function parseAmount(amountStr: string | undefined): number {
  if (!amountStr) return 0;
  // Remove currency symbols, commas, spaces, and other non-numeric chars (except . and -)
  const cleaned = amountStr.replace(/[^0-9.-]/g, '');
  const parsed = parseFloat(cleaned);
  return Number.isNaN(parsed) ? 0 : parsed;
}

// ============================================================================
// Sortable Case Card Component
// ============================================================================

interface SortableCaseCardProps {
  queue: QueueModel;
  isActiveCall: boolean;
  isCurrentCase: boolean;
  onSelect: (queue: QueueModel) => void;
  onMoveToTop: (id: number) => void;
  onDelete: (id: number) => void;
}

function SortableCaseCard({ queue, isActiveCall, isCurrentCase, onSelect, onMoveToTop, onDelete }: SortableCaseCardProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: queue.id.toString(),
    disabled: isActiveCall, // Disable dragging for active calls
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // Map QueueModel to CaseCard props
  const overdueAmount = parseAmount(queue.amount_due);
  const hasPTP = queue.tags?.some(tag => tag.toLowerCase().includes('ptp')) ?? false;
  const ptpCount = queue.tags?.filter(tag => tag.toLowerCase().includes('ptp')).length ?? 0;

  // Build drag props only if not active call
  const dragProps = isActiveCall
    ? {}
    : {
        dragListeners: listeners,
        dragAttributes: attributes,
      };

  return (
    <CaseCard
      ref={setNodeRef}
      caseId={queue.id.toString()}
      hirerName={queue.name}
      tier={queue.tier}
      overdueAmount={overdueAmount}
      hasPTP={hasPTP}
      ptpCount={ptpCount}
      isActive={isCurrentCase}
      isActiveCall={isActiveCall}
      isDragging={isDragging}
      status={queue.status}
      onClick={() => onSelect(queue)}
      onMoveToTop={() => onMoveToTop(queue.id)}
      onDelete={() => onDelete(queue.id)}
      style={style}
      {...dragProps}
    />
  );
}

// ============================================================================
// Component
// ============================================================================

interface SidebarQueuePanelProps {
  collapsed: boolean;
}

export const SidebarQueuePanel = memo(function SidebarQueuePanel({ collapsed }: SidebarQueuePanelProps) {
  const theme = useMuiTheme();
  const navigate = useNavigate();
  const workTimeType = useWorkTimeType();
  const { activeTab, setActiveTab, currentCaseId, setCurrentCase } = useQueueStore();

  // Local queue state for optimistic updates
  const [localQueues, setLocalQueues] = useState<QueueModel[]>([]);
  const [isReordering, setIsReordering] = useState(false);

  // Summary modal state for ended queues
  const [summaryModalOpen, setSummaryModalOpen] = useState(false);
  const [selectedQueueData, setSelectedQueueData] = useState<QueueModel | null>(null);
  const isReorderingRef = useRef(false);

  // Keep ref in sync with state
  useEffect(() => {
    isReorderingRef.current = isReordering;
  }, [isReordering]);

  // React Query
  const { data: queueResponse, isLoading, refetch } = useQueueList(sidebarRequest);
  const moveQueueMutation = useMoveQueue();
  const deleteQueueMutation = useDeleteQueues();
  const { mutate: makeNextCall, isPending: isNextCallPending } = useMakeNextCall();
  const toast = useToast();

  // Socket.IO
  const { subscribe, isConnected } = useSocketIOContext();

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Update local queues when response changes
  useEffect(() => {
    if (queueResponse?.queues) {
      // Sort by order (ascending)
      const sorted = [...queueResponse.queues].sort((a, b) => a.order - b.order);
      setLocalQueues(sorted);
    }
  }, [queueResponse]);

  // Socket.IO subscription for v2 call events (created + status)
  useEffect(() => {
    if (!isConnected) return;

    const unsubscribe = subscribe(event => {
      // Only handle /call namespace events
      if (event.namespace !== SocketIONamespace.Call) return;
      // Skip updates during reordering
      if (isReorderingRef.current) return;

      // created — new call added (queue or incoming)
      if (event.event === 'created') {
        const mapped = v2CallResponseToQueueModel(event.data as V2CallResponse);
        setLocalQueues(prev => {
          const idx = prev.findIndex(q => q.id === mapped.id);
          if (idx !== -1) {
            const next = [...prev];
            next[idx] = mapped;
            return next.sort((a, b) => a.order - b.order);
          }
          return [...prev, mapped].sort((a, b) => a.order - b.order);
        });
      }

      // status — call status changed
      if (event.event === 'status') {
        const data = event.data as CallStatusEventData;
        const shouldRemove =
          data.status === 'cancelled' ||
          data.status === 'ended' ||
          data.status === 'closed' ||
          data.status === 'forwarded' ||
          data.status === 'follow_up' ||
          data.status === 'failed';

        setLocalQueues(prev => {
          const idx = prev.findIndex(q => q.id === data.call_id);
          if (idx === -1) return prev;
          if (shouldRemove) {
            const next = [...prev];
            next.splice(idx, 1);
            setTimeout(() => { void refetch(); }, 1500);
            return next;
          }
          const next = [...prev];
          next[idx] = { ...next[idx], status: data.status as QueueModel['status'] };
          return next;
        });
      }
    });

    return unsubscribe;
  }, [subscribe, isConnected, refetch]);

  // Calculate target order for reordering
  const calculateTargetOrder = useCallback((newIndex: number, queueList: QueueModel[]): number => {
    const activeCall = queueList.find(q => q.status.toLowerCase() === 'active');

    if (activeCall) {
      const activeCallOrder = activeCall.order;
      if (newIndex === 0) {
        // Moving to first position = position after active call
        return activeCallOrder + 1;
      } else {
        // Moving to other positions
        return newIndex + activeCallOrder + 1;
      }
    } else {
      // No active call, simple 1-based ordering
      return newIndex + 1;
    }
  }, []);

  // Handle drag end
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over || active.id === over.id) {
        return;
      }

      const oldIndex = localQueues.findIndex(q => q.id.toString() === active.id);
      const newIndex = localQueues.findIndex(q => q.id.toString() === over.id);

      if (oldIndex === -1 || newIndex === -1) {
        return;
      }

      const item = localQueues[oldIndex];
      const isActiveCall = item.status.toLowerCase() === 'active';

      // Prevent reordering if already in progress or item is active
      if (isReordering || isActiveCall) {
        return;
      }

      // Adjust index if moving down
      let adjustedNewIndex = newIndex;
      if (newIndex > oldIndex) {
        adjustedNewIndex -= 1;
      }

      const targetOrder = calculateTargetOrder(adjustedNewIndex, localQueues);

      // Optimistic UI update
      setIsReordering(true);
      const updatedList = arrayMove(localQueues, oldIndex, adjustedNewIndex);
      setLocalQueues(updatedList);

      // Send reorder request
      moveQueueMutation.mutate(
        {
          queue_and_order: [
            {
              queue_id: item.id,
              target_order: targetOrder,
            },
          ],
          paginated: sidebarRequest,
        },
        {
          onSuccess: () => {
            // Reset reordering flag after successful move
            setTimeout(() => {
              setIsReordering(false);
            }, 2000);
          },
          onError: () => {
            // On error, reset flags and reload data
            setIsReordering(false);
            void refetch();
          },
        }
      );
    },
    [localQueues, isReordering, calculateTargetOrder, moveQueueMutation, refetch]
  );

  // Handle card click - open summary modal for ended queues, otherwise navigate to case detail
  const handleCardClick = useCallback(
    (queue: QueueModel) => {
      if (queue.status === 'ended') {
        setSelectedQueueData(queue);
        setSummaryModalOpen(true);
      } else {
        const caseId = queue.case_id?.toString() ?? '';
        setCurrentCase(caseId as any);
        navigate(`/case-detail/${caseId}`, {
          state: {
            callee: queue.callee ?? '',
            loan_id: queue.loan_id ?? '',
            entity: queue.entity ?? '',
            amount_due: queue.amount_due ?? '',
            dpd: queue.dpd ?? 0,
          },
        });
      }
    },
    [setCurrentCase, navigate]
  );

  const handleSummaryModalClose = useCallback(() => {
    setSummaryModalOpen(false);
    setSelectedQueueData(null);
  }, []);

  // Handle move to top
  const handleMoveToTop = useCallback(
    (queueId: number) => {
      moveQueueMutation.mutate(
        {
          queue_and_order: [
            {
              queue_id: queueId,
              target_order: 1, // Move to top (order 1)
            },
          ],
          paginated: sidebarRequest,
        },
        {
          onSuccess: () => {
            void refetch();
          },
        }
      );
    },
    [moveQueueMutation, refetch]
  );

  // Handle delete queue item
  const handleDeleteQueue = useCallback(
    (queueId: number) => {
      const deleteRequest: CallQueueDeleteRequest = {
        id: queueId,
        status: 'cancelled',
      };

      // Optimistic UI update - remove from local state
      setLocalQueues(prev => prev.filter(q => q.id !== queueId));

      deleteQueueMutation.mutate([deleteRequest], {
        onError: () => {
          // On error, reload data to restore
          void refetch();
        },
      });
    },
    [deleteQueueMutation, refetch]
  );

  // Separate queues into active calls and non-active
  const { activeQueues, nonActiveQueues } = useMemo(() => {
    const active: QueueModel[] = [];
    const nonActive: QueueModel[] = [];

    localQueues.forEach(queue => {
      if (queue.status.toLowerCase() === 'active') {
        active.push(queue);
      } else {
        nonActive.push(queue);
      }
    });

    return { activeQueues: active, nonActiveQueues: nonActive };
  }, [localQueues]);

  // Sortable item IDs (only non-active)
  const sortableIds = useMemo(() => nonActiveQueues.map(q => q.id.toString()), [nonActiveQueues]);

  const tabIndex = activeTab === QueueTab.Queue ? 0 : activeTab === QueueTab.FollowUps ? 1 : 2;

  const queueCount = localQueues.length;

  // Show "Proceed to next call" only when no call is in progress and user is working
  const hasActiveCall = localQueues.some(q => {
    const s = q.status.toLowerCase();
    return s === 'active' || s === 'dialing' || s === 'ringing' || s === 'forwarding';
  });
  const showNextCallLink = !hasActiveCall && workTimeType === 'working' && queueCount > 0;

  const handleNextCall = () => {
    makeNextCall(undefined, {
      onError: (error: unknown) => {
        console.error('Failed to proceed to next call:', error);
        toast.error('Failed to proceed to next call');
      },
    });
  };

  // Global keyboard accelerator for the "Proceed to next call" action.
  useTelephonyHotkeys({
    onNextCall: showNextCallLink && !isNextCallPending ? handleNextCall : undefined,
  });

  // Helper to get status color based on work time type
  const getStatusColor = () => {
    switch (workTimeType) {
      case 'online':
        return 'success.main';
      case 'working':
        return 'primary.main';
      case 'paused':
        return 'warning.main';
      case 'offline':
      default:
        return 'text.secondary';
    }
  };

  const getStatusBgColor = () => {
    switch (workTimeType) {
      case 'online':
        return alpha(theme.palette.success.main, 0.12);
      case 'working':
        return alpha(theme.palette.primary.main, 0.12);
      case 'paused':
        return alpha(theme.palette.warning.main, 0.12);
      case 'offline':
      default:
        return 'background.default';
    }
  };

  // Collapsed state - show just a queue icon with tooltip
  if (collapsed) {
    return (
      <Box
        sx={{
          flex: 1, // Takes remaining space
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          py: 2,
          gap: 1,
        }}
      >
        <Tooltip title="Queue" placement="right" arrow>
          <IconButton
            size="small"
            sx={{
              bgcolor: getStatusBgColor(),
              color: getStatusColor(),
            }}
            aria-label="Queue"
          >
            <QueueIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={`${String(queueCount)} cases`} placement="right" arrow>
          <Chip
            label={queueCount}
            size="small"
            sx={{
              height: 20,
              fontSize: '0.625rem',
              minWidth: 28,
            }}
          />
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
      }}
    >
      {/* Tabs */}
      <Tabs
        value={tabIndex}
        onChange={(_, v) => {
          const tabs = [QueueTab.Queue, QueueTab.FollowUps, QueueTab.History];
          setActiveTab(tabs[v]);
        }}
        variant="fullWidth"
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          minHeight: 40,
          flexShrink: 0,
          '& .MuiTab-root': {
            minHeight: 40,
            py: 1,
            px: 1,
            fontSize: '0.75rem',
            whiteSpace: 'nowrap',
          },
        }}
      >
        <Tab
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              Queue
              <Chip label={queueCount} size="small" sx={{ height: 16, fontSize: '0.5625rem', ml: 0.5 }} />
            </Box>
          }
        />
        <Tab label="Follow-ups" />
        <Tab label="History" />
      </Tabs>

      {/* Work Time Controls - only show for Queue tab */}
      {activeTab === QueueTab.Queue && <WorkTimeToggle />}

      {/* Tab Content */}
      {activeTab === QueueTab.FollowUps ? (
        // Follow-ups tab content
        <FollowUpList />
      ) : activeTab === QueueTab.History ? (
        <CallHistoryCardList />
      ) : (
        // Queue tab content (default)
        <>
          {/* Status */}
          <Box
            sx={{
              px: 1.5,
              py: 0.75,
              bgcolor: alpha(theme.palette.background.default, 0.5),
              borderBottom: 1,
              borderColor: 'divider',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {queueCount} cases in queue
            </Typography>
            {showNextCallLink && (
              <Link
                component="button"
                variant="caption"
                onClick={handleNextCall}
                sx={{
                  cursor: isNextCallPending ? 'default' : 'pointer',
                  opacity: isNextCallPending ? 0.6 : 1,
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
                disabled={isNextCallPending}
              >
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  {isNextCallPending ? 'Calling...' : 'Proceed to next call'}
                  <ShortcutBadge label="Alt+N" />
                </Box>
              </Link>
            )}
          </Box>

          {/* Case List - flows in parent scroll (no inner scroll) */}
          <Box
            sx={{
              p: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.75,
            }}
          >
            {isLoading && localQueues.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  py: 4,
                }}
              >
                <CircularProgress size={24} />
              </Box>
            ) : localQueues.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  py: 4,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  No cases in queue
                </Typography>
              </Box>
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                {/* Active calls (non-sortable, rendered first) */}
                {activeQueues.map(queue => {
                  const overdueAmount = parseAmount(queue.amount_due);
                  const hasPTP = queue.tags?.some(tag => tag.toLowerCase().includes('ptp')) ?? false;
                  const ptpCount = queue.tags?.filter(tag => tag.toLowerCase().includes('ptp')).length ?? 0;

                  return (
                    <CaseCard
                      key={queue.id}
                      caseId={queue.id.toString()}
                      hirerName={queue.name}
                      tier={queue.tier}
                      overdueAmount={overdueAmount}
                      hasPTP={hasPTP}
                      ptpCount={ptpCount}
                      isActive={queue.case_id?.toString() === currentCaseId}
                      isActiveCall={true}
                      status={queue.status}
                      onClick={() => handleCardClick(queue)}
                      onMoveToTop={() => handleMoveToTop(queue.id)}
                      onDelete={() => handleDeleteQueue(queue.id)}
                      sx={{
                        borderColor: 'success.main',
                        bgcolor: alpha(theme.palette.success.main, 0.08),
                      }}
                    />
                  );
                })}

                {/* Non-active calls (sortable) */}
                <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
                  {nonActiveQueues.map(queue => (
                    <SortableCaseCard
                      key={queue.id}
                      queue={queue}
                      isActiveCall={false}
                      isCurrentCase={queue.case_id?.toString() === currentCaseId}
                      onSelect={handleCardClick}
                      onMoveToTop={handleMoveToTop}
                      onDelete={handleDeleteQueue}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            )}
          </Box>
        </>
      )}
      {/* Phone Call Summary Modal for ended queues */}
      <PhoneCallSummaryModal open={summaryModalOpen} onClose={handleSummaryModalClose} queueData={selectedQueueData} />
    </Box>
  );
});
